/* ResourceForm — the descriptor's `fields` as an antd form, in the /profile page's visual language.
 *
 * `<Form component={false}>`: the item lives in `ResourcePage`'s state and every renderer is a
 * controlled control calling `onChange(name, value)`, so antd's own field store is deliberately
 * not used — `<Form.Item>` is here for the label, the required marker and the error, which is the
 * one part every field otherwise had to draw for itself. That is also why the renderers no longer
 * carry a `<label>`: it lives in exactly one place now.
 *
 * The look mirrors `/profile` (the user asked for it): each descriptor section is a card with a
 * plain title + optional one-line description (not an icon eyebrow), and the controls are
 * `size="large"` — set once via a scoped `ConfigProvider` so every renderer inherits it rather
 * than threading a size prop through each. Fields tile on `.pt-fieldgrid`, the mockups' `.edform`
 * auto-fit grid, with `wide`/`tall` descriptor fields spanning the row.
 */
import React from "react";
import { Card, ConfigProvider, Flex, Form } from "antd";

import { FIELD_RENDERERS } from "./fields";
import { POINTS_ADMIN } from "../../config/lang";
import { translate } from "../../util/util";

/** The card's head — a title and, when the section declares one, a one-line description
 *  (profile's `pf-card__head`). */
function SectionHeader({ title, desc }) {
  return (
    <div className="dp-sec-head">
      <h2 className="dp-sec-title">{title}</h2>
      {desc ? <p className="dp-sec-desc">{desc}</p> : null}
    </div>
  );
}

/** Group a descriptor's fields by its `sections`, resolving field names, the section label and its
 *  optional `desc` key. Falls back to one untitled group when a descriptor declares no sections. */
function groupBySection(descriptor, lang) {
  const byName = {};
  (descriptor.fields || []).forEach(field => {
    byName[field.name] = field;
  });
  if (Array.isArray(descriptor.sections) && descriptor.sections.length) {
    return descriptor.sections.map(section => ({
      key: section.title,
      title: translate(POINTS_ADMIN, section.title, lang) || section.title,
      desc: section.desc ? translate(POINTS_ADMIN, section.desc, lang) || section.desc : null,
      fields: section.fields.map(name => byName[name]).filter(Boolean),
    }));
  }
  return [{ key: null, title: null, desc: null, fields: descriptor.fields || [] }];
}

function FieldItem({ field, item, errors, onChange, lang, labels }) {
  const Renderer = FIELD_RENDERERS[field.type];
  if (!Renderer) return null;

  const label = translate(POINTS_ADMIN, field.label, lang) || field.label;
  const error = errors[field.name];

  return (
    /* `data-field` is how a failed save finds the first invalid control to focus
       (spec/03#failed-save) — antd's own field store is not in play here. A `wide` field (or a
       tall one, like the point list) spans the whole grid row; the rest tile. */
    <div data-field={field.name} className={field.wide || field.tall ? "pt-field pt-field--wide" : "pt-field"}>
      <Form.Item
        /* A checkbox names itself beside the box, so the real label is suppressed — but the row is
           kept as a non-breaking-space spacer, not dropped, so the box lines up with labelled
           fields around it. */
        label={field.type === "checkbox" ? " " : label}
        required={!!field.required}
        validateStatus={error ? "error" : undefined}
        help={error || undefined}>
        <Renderer field={{ ...field, _label: label }} value={item[field.name]} item={item} lang={lang} labels={labels} onChange={onChange} error={error} />
      </Form.Item>
    </div>
  );
}

export default function ResourceForm({ descriptor, item, errors = {}, onChange, lang }) {
  const t = key => translate(POINTS_ADMIN, key, lang);
  const labels = {
    lat: t("f_lat"),
    lng: t("f_lng"),
    pickImage: t("pick_image"),
    removeImage: t("remove_image"),
    crop: t("crop_confirm"),
    cropConfirm: t("crop_confirm"),
    cropCancel: t("crop_cancel"),
    cropTitle: t("crop_title"),
    cropSize: t("crop_size"),
    cropPreview: t("crop_preview"),
    cropArea: t("crop_area"),
    cropHint: t("crop_hint"),
  };
  const ctx = { item, errors, onChange, lang, labels };

  /* `heroField` (points → `location`) is hoisted to a headerless hero card at the top of the form
     and dropped from its normal section, so the map leads the edit page. Optional. */
  const heroName = descriptor.heroField;
  const heroField = heroName ? (descriptor.fields || []).find(f => f.name === heroName) : null;

  const sections = groupBySection(descriptor, lang)
    .map(section => ({ ...section, fields: section.fields.filter(f => f.name !== heroName) }))
    .filter(section => section.fields.length);

  return (
    /* Large controls, profile-style — scoped here so the page's own chrome (head, save bar) keeps
       the default size. */
    <ConfigProvider componentSize="large">
      {/* ⚠ `dp-form` goes on the Flex (a real div), NOT on the <Form>: `component={false}` renders no
          DOM node at all, so a className there is dropped silently — and with it
          `detail-page.scss`'s label weight and the `width: 100%` that makes every control fill its
          cell. That is exactly how this form ended up rendering at two widths per row. Verify with
          `document.querySelectorAll(".dp-form").length`, not by reading the JSX. */}
      <Form layout="vertical" component={false} colon={false} requiredMark>
        <Flex vertical gap="middle" className="dp-form">
          {heroField && (
            <Card className="dp-section pt-herocard" variant="outlined">
              <FieldItem field={heroField} {...ctx} />
            </Card>
          )}
          {sections.map((section, index) => (
            <Card
              key={section.key || index}
              className="dp-section"
              variant="outlined"
              title={section.title ? <SectionHeader title={section.title} desc={section.desc} /> : undefined}>
              {/* Fields tile to fill the full-width card (the mockup's `.edform` grid); a wide/tall
                  field spans the row. */}
              <div className="pt-fieldgrid">
                {section.fields.map(field => (
                  <FieldItem key={field.name} field={field} {...ctx} />
                ))}
              </div>
            </Card>
          ))}
        </Flex>
      </Form>
    </ConfigProvider>
  );
}
